import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Store, Settings, Package, ShoppingBag, Eye, Copy, ExternalLink, Upload, Save, X, Calendar, LayoutDashboard, Send, Check, ChevronRight, ChevronLeft } from 'lucide-react';
import api from '../utils/api';

// ============= SHOP SETUP WIZARD =============
const ShopSetupWizard = ({ user, onCreated }) => {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [createdShop, setCreatedShop] = useState(null);
  const [data, setData] = useState({
    shop_name: user?.company_name || '',
    description: '',
    contact_name: user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : '',
    contact_email: user?.email || '',
    contact_phone: user?.phone || '',
    whatsapp_phone: '',
    street: '',
    postal_code: '',
    city: '',
  });

  const step1Valid = data.shop_name.trim().length >= 2;
  const step3Valid = data.contact_name.trim() && data.contact_email.trim();

  const createShop = async () => {
    setSaving(true);
    try {
      const res = await api.post('/reseller/shop', { shop_name: data.shop_name });
      setCreatedShop(res.data.shop);
      // Save description immediately if provided
      if (data.description) {
        await api.put('/reseller/shop', { shop_name: data.shop_name, description: data.description });
      }
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async (file, endpoint) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Hochgeladen');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const finish = async () => {
    setSaving(true);
    try {
      const res = await api.put('/reseller/shop', {
        shop_name: data.shop_name,
        description: data.description,
        contact_name: data.contact_name,
        contact_email: data.contact_email,
        contact_phone: data.contact_phone,
        whatsapp_phone: data.whatsapp_phone,
        street: data.street,
        postal_code: data.postal_code,
        city: data.city,
      });
      onCreated(res.data.shop);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="shop-setup-wizard">
      <div className="mb-6 flex items-center gap-3">
        {[1, 2, 3].map(n => (
          <div key={n} className="flex-1 flex items-center gap-2">
            <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold ${
              step > n ? 'bg-green-600 text-white' :
              step === n ? 'bg-primary text-white' :
              'bg-gray-200 text-gray-500'
            }`}>
              {step > n ? <Check className="h-4 w-4" /> : n}
            </div>
            {n < 3 && <div className={`h-1 flex-1 rounded ${step > n ? 'bg-green-600' : 'bg-gray-200'}`} />}
          </div>
        ))}
      </div>

      <div className="bg-white border border-border rounded-lg p-6">
        {/* Step 1 – Basics */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <Store className="h-12 w-12 mx-auto text-primary mb-2" />
              <h1 className="text-2xl font-bold">Willkommen! Richten wir Ihren Shop ein.</h1>
              <p className="text-gray-600">Schritt 1 von 3: Grundlegende Angaben</p>
            </div>
            <div>
              <Label>Shop-Name *</Label>
              <Input value={data.shop_name} onChange={(e) => setData({ ...data, shop_name: e.target.value })} placeholder="z.B. Ali Barbershop" data-testid="wizard-shop-name-input" />
              <p className="text-xs text-gray-500 mt-1">So sehen Ihre Kunden den Shop.</p>
            </div>
            <div>
              <Label>Kurzbeschreibung (optional)</Label>
              <textarea value={data.description} onChange={(e) => setData({ ...data, description: e.target.value })} rows={3} className="w-full border rounded p-2" placeholder="z.B. Ihr Barbershop im Herzen von Berlin – Termin online buchen." data-testid="wizard-description-input" />
            </div>
            <div className="flex justify-end pt-4">
              <Button onClick={createShop} disabled={!step1Valid || saving} data-testid="wizard-step1-next">
                {saving ? 'Wird erstellt…' : 'Shop anlegen & weiter'} <ChevronRight className="h-4 w-4 ms-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2 – Logo/Banner */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <Upload className="h-12 w-12 mx-auto text-primary mb-2" />
              <h1 className="text-2xl font-bold">Branding hochladen</h1>
              <p className="text-gray-600">Schritt 2 von 3: Logo &amp; Banner (optional)</p>
            </div>
            <div>
              <Label>Logo (quadratisch, mind. 200×200 px)</Label>
              <input type="file" accept="image/*" onChange={(e) => uploadFile(e.target.files[0], '/reseller/shop/upload-logo')} disabled={uploading} className="block w-full text-sm" data-testid="wizard-logo-upload" />
            </div>
            <div>
              <Label>Banner (breit, empfohlen 1200×400 px)</Label>
              <input type="file" accept="image/*" onChange={(e) => uploadFile(e.target.files[0], '/reseller/shop/upload-banner')} disabled={uploading} className="block w-full text-sm" data-testid="wizard-banner-upload" />
            </div>
            <p className="text-xs text-gray-500">Sie können diese Schritte jederzeit später ergänzen.</p>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="wizard-step2-prev">
                <ChevronLeft className="h-4 w-4 me-1" /> Zurück
              </Button>
              <Button onClick={() => setStep(3)} data-testid="wizard-step2-next">
                Weiter <ChevronRight className="h-4 w-4 ms-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 – Contact */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <Store className="h-12 w-12 mx-auto text-primary mb-2" />
              <h1 className="text-2xl font-bold">Kontakt &amp; Adresse</h1>
              <p className="text-gray-600">Schritt 3 von 3: Damit Kunden Sie erreichen</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Ansprechpartner *</Label>
                <Input value={data.contact_name} onChange={(e) => setData({ ...data, contact_name: e.target.value })} data-testid="wizard-contact-name" />
              </div>
              <div>
                <Label>E-Mail *</Label>
                <Input type="email" value={data.contact_email} onChange={(e) => setData({ ...data, contact_email: e.target.value })} data-testid="wizard-contact-email" />
              </div>
              <div>
                <Label>Telefon</Label>
                <Input value={data.contact_phone} onChange={(e) => setData({ ...data, contact_phone: e.target.value })} data-testid="wizard-contact-phone" />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input value={data.whatsapp_phone} onChange={(e) => setData({ ...data, whatsapp_phone: e.target.value })} data-testid="wizard-whatsapp" />
              </div>
              <div className="md:col-span-2">
                <Label>Straße</Label>
                <Input value={data.street} onChange={(e) => setData({ ...data, street: e.target.value })} data-testid="wizard-street" />
              </div>
              <div>
                <Label>PLZ</Label>
                <Input value={data.postal_code} onChange={(e) => setData({ ...data, postal_code: e.target.value })} data-testid="wizard-postal" />
              </div>
              <div>
                <Label>Ort</Label>
                <Input value={data.city} onChange={(e) => setData({ ...data, city: e.target.value })} data-testid="wizard-city" />
              </div>
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ChevronLeft className="h-4 w-4 me-1" /> Zurück
              </Button>
              <Button onClick={finish} disabled={!step3Valid || saving} data-testid="wizard-finish">
                {saving ? 'Speichere…' : 'Einrichtung abschließen'} <Check className="h-4 w-4 ms-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MyShopPage = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  
  const [formData, setFormData] = useState({
    shop_name: '',
    description: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    street: '',
    postal_code: '',
    city: '',
    country: 'Deutschland',
    whatsapp_phone: '',
    is_active: false
  });

  useEffect(() => {
    fetchShop();
  }, []);

  const fetchShop = async () => {
    try {
      const response = await api.get('/reseller/shop');
      if (response.data.exists) {
        setShop(response.data.shop);
        setFormData({
          shop_name: response.data.shop.shop_name || '',
          description: response.data.shop.description || '',
          contact_name: response.data.shop.contact_name || '',
          contact_email: response.data.shop.contact_email || '',
          contact_phone: response.data.shop.contact_phone || '',
          street: response.data.shop.street || '',
          postal_code: response.data.shop.postal_code || '',
          city: response.data.shop.city || '',
          country: response.data.shop.country || 'Deutschland',
          whatsapp_phone: response.data.shop.whatsapp_phone || '',
          is_active: response.data.shop.is_active || false
        });
      }
    } catch (error) {
      console.error('Error fetching shop:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateShop = async () => {
    setCreating(true);
    try {
      const response = await api.post('/reseller/shop', { shop_name: user?.company_name || 'Mein Shop' });
      setShop(response.data.shop);
      setFormData({
        shop_name: response.data.shop.shop_name || '',
        description: response.data.shop.description || '',
        contact_name: response.data.shop.contact_name || '',
        contact_email: response.data.shop.contact_email || '',
        contact_phone: response.data.shop.contact_phone || '',
        street: response.data.shop.street || '',
        postal_code: response.data.shop.postal_code || '',
        city: response.data.shop.city || '',
        country: response.data.shop.country || 'Deutschland',
        whatsapp_phone: response.data.shop.whatsapp_phone || '',
        is_active: response.data.shop.is_active || false
      });
      toast.success('Shop erfolgreich erstellt!');
    } catch (error) {
      toast.error('Fehler beim Erstellen des Shops');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const response = await api.put('/reseller/shop', formData);
      setShop(response.data.shop);
      toast.success('Einstellungen gespeichert!');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/reseller/shop/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Logo hochgeladen!');
      fetchShop();
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    }
  };

  const handleUploadBanner = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/reseller/shop/upload-banner', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Banner hochgeladen!');
      fetchShop();
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    }
  };

  const copyShopLink = () => {
    const link = `${window.location.origin}/shop/${shop?.shop_slug}`;
    navigator.clipboard.writeText(link);
    toast.success('Link kopiert!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // No shop yet - show 3-step wizard
  if (!shop) {
    return <ShopSetupWizard user={user} onCreated={(s) => { setShop(s); setFormData({...formData, shop_name: s.shop_name}); toast.success('Shop erfolgreich eingerichtet!'); }} />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Store className="h-6 w-6" />
            Mein Endkundenshop
          </h1>
          <p className="text-gray-600 mt-1">
            Verwalten Sie Ihren B2C-Shop für Endkunden
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-sm ${shop.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
            {shop.is_active ? 'Aktiv' : 'Inaktiv'}
          </span>
          <Button variant="outline" onClick={copyShopLink} data-testid="copy-link-btn">
            <Copy className="h-4 w-4 me-1" />
            Link kopieren
          </Button>
          <Button 
            variant="outline" 
            onClick={() => window.open(`/shop/${shop.shop_slug}`, '_blank')}
            disabled={!shop.is_active}
            data-testid="view-shop-btn"
          >
            <ExternalLink className="h-4 w-4 me-1" />
            Shop ansehen
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-4 -mb-px overflow-x-auto">
          <button
            onClick={() => navigate('/my-shop/dashboard')}
            className="pb-3 px-1 border-b-2 border-transparent text-gray-500 hover:text-gray-700 font-medium text-sm whitespace-nowrap"
            data-testid="tab-dashboard"
          >
            <LayoutDashboard className="h-4 w-4 inline me-1" />
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'settings' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-settings"
          >
            <Settings className="h-4 w-4 inline me-1" />
            Einstellungen
          </button>
          <button
            onClick={() => navigate('/my-shop/products')}
            className="pb-3 px-1 border-b-2 border-transparent text-gray-500 hover:text-gray-700 font-medium text-sm"
            data-testid="tab-products"
          >
            <Package className="h-4 w-4 inline me-1" />
            Produkte & Preise
          </button>
          <button
            onClick={() => navigate('/my-shop/orders')}
            className="pb-3 px-1 border-b-2 border-transparent text-gray-500 hover:text-gray-700 font-medium text-sm"
            data-testid="tab-orders"
          >
            <ShoppingBag className="h-4 w-4 inline me-1" />
            B2C-Bestellungen
          </button>
          <button
            onClick={() => navigate('/my-shop/appointments')}
            className="pb-3 px-1 border-b-2 border-transparent text-gray-500 hover:text-gray-700 font-medium text-sm"
            data-testid="tab-appointments"
          >
            <Calendar className="h-4 w-4 inline me-1" />
            Terminbuchung
          </button>
          <button
            onClick={() => navigate('/my-shop/marketing')}
            className="pb-3 px-1 border-b-2 border-transparent text-gray-500 hover:text-gray-700 font-medium text-sm"
            data-testid="tab-marketing"
          >
            <Send className="h-4 w-4 inline me-1" />
            Marketing &amp; Angebote
          </button>
        </nav>
      </div>

      {/* Settings Form */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Shop Info */}
          <div className="bg-white border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Shop-Informationen</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="shop_name">Shopname *</Label>
                <Input
                  id="shop_name"
                  value={formData.shop_name}
                  onChange={(e) => setFormData({...formData, shop_name: e.target.value})}
                  placeholder="z.B. Barbershop Mustermann"
                  className="mt-1"
                  data-testid="shop-name-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="shop_slug">Shop-Link</Label>
                <div className="flex mt-1">
                  <span className="inline-flex items-center px-3 bg-gray-100 border border-e-0 border-gray-300 rounded-s-md text-gray-500 text-sm">
                    {window.location.origin}/shop/
                  </span>
                  <Input
                    id="shop_slug"
                    value={shop.shop_slug}
                    disabled
                    className="rounded-s-none"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="description">Kurzbeschreibung</Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Beschreiben Sie Ihren Shop..."
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  rows={3}
                  data-testid="description-input"
                />
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-white border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Kontaktdaten</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_name">Ansprechpartner</Label>
                <Input
                  id="contact_name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                  className="mt-1"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <Label htmlFor="contact_email">E-Mail</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  className="mt-1"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <Label htmlFor="contact_phone">Telefon</Label>
                <Input
                  id="contact_phone"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                  className="mt-1"
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="whatsapp_phone">WhatsApp (für Benachrichtigungen)</Label>
                <Input
                  id="whatsapp_phone"
                  value={formData.whatsapp_phone}
                  onChange={(e) => setFormData({...formData, whatsapp_phone: e.target.value})}
                  placeholder="+49 171 1234567"
                  className="mt-1"
                  data-testid="whatsapp-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="street">Straße & Hausnummer</Label>
                <Input
                  id="street"
                  value={formData.street}
                  onChange={(e) => setFormData({...formData, street: e.target.value})}
                  className="mt-1"
                  data-testid="street-input"
                />
              </div>
              <div>
                <Label htmlFor="postal_code">PLZ</Label>
                <Input
                  id="postal_code"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                  className="mt-1"
                  data-testid="postal-input"
                />
              </div>
              <div>
                <Label htmlFor="city">Stadt</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="mt-1"
                  data-testid="city-input"
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-2">
            <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-settings-btn">
              <Save className="h-4 w-4 me-1" />
              {saving ? 'Speichern...' : 'Einstellungen speichern'}
            </Button>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Logo Upload */}
          <div className="bg-white border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Logo</h2>
            {shop.logo_url ? (
              <div className="mb-4">
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${shop.logo_url}`} 
                  alt="Shop Logo" 
                  className="w-32 h-32 object-contain border rounded"
                />
              </div>
            ) : (
              <div className="w-32 h-32 bg-gray-100 border rounded flex items-center justify-center mb-4">
                <Store className="h-12 w-12 text-gray-400" />
              </div>
            )}
            <label className="cursor-pointer">
              <Input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleUploadLogo}
                className="hidden"
              />
              <Button variant="outline" size="sm" asChild>
                <span>
                  <Upload className="h-4 w-4 me-1" />
                  Logo hochladen
                </span>
              </Button>
            </label>
          </div>

          {/* Banner Upload */}
          <div className="bg-white border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Banner</h2>
            {shop.banner_url ? (
              <div className="mb-4">
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${shop.banner_url}`} 
                  alt="Shop Banner" 
                  className="w-full h-24 object-cover border rounded"
                />
              </div>
            ) : (
              <div className="w-full h-24 bg-gray-100 border rounded flex items-center justify-center mb-4">
                <span className="text-gray-400 text-sm">Kein Banner</span>
              </div>
            )}
            <label className="cursor-pointer">
              <Input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleUploadBanner}
                className="hidden"
              />
              <Button variant="outline" size="sm" asChild>
                <span>
                  <Upload className="h-4 w-4 me-1" />
                  Banner hochladen
                </span>
              </Button>
            </label>
          </div>

          {/* Shop Status */}
          <div className="bg-white border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Shop-Status</h2>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                data-testid="shop-active-toggle"
              />
              <div>
                <span className="font-medium">Shop aktivieren</span>
                <p className="text-sm text-gray-500">
                  Nur aktive Shops sind öffentlich sichtbar
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyShopPage;
